import { create } from 'zustand';
import api from 'api';
import secureLocalStorage from 'react-secure-storage';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { convertConfigToPermissions, createEmptyRolePermissionTemplate } from 'config/routeTabConfig';
import { LEAD_DETAILS_COMPONENTS } from 'config/leadDetailsComponents';

const KEY_MIGRATION_MAP = {
  discountCode: 'discount-codes',
  rbacManagement: 'rbac-management',
  ticketGroup: 'ticket-group-management',
  clinicManagement: 'clinic-management',
  offlineDoctorRoaster: 'offline-doctor-roaster',
  webinar: 'webinar-management',
  offlineHairTestConfig: 'offline-hairtest',
  medicine: 'medicines',
  productMapping: 'product-mapping',
};

/**
 * Merge API permissions with default config.
 * - New routes/subroutes from config are added with allowed: true
 * - Existing API values (allowed) are preserved
 * - rbacEnabled ALWAYS comes from config (code-level decision)
 */
export const mergeWithDefaults = (apiPerms, defaultPerms) => {
  const merged = JSON.parse(JSON.stringify(defaultPerms));

  // Preserve __settings (e.g., defaultRoute) from API permissions
  if (apiPerms.__settings) {
    merged.__settings = JSON.parse(JSON.stringify(apiPerms.__settings));
  }

  Object.entries(apiPerms).forEach(([groupKey, apiGroup]) => {
    if (!merged[groupKey]) {
      // Skip groups from DB that no longer exist in config (removed routes)
      return;
    }

    if (apiGroup.allowed !== undefined) merged[groupKey].allowed = apiGroup.allowed;

    if (!apiGroup.routes) return;

    Object.entries(apiGroup.routes).forEach(([routeKey, apiRoute]) => {
      if (!merged[groupKey].routes[routeKey]) {
        // Skip routes from DB that no longer exist in config (removed routes)
        return;
      }

      const mergedRoute = merged[groupKey].routes[routeKey];

      if (apiRoute.allowed !== undefined) mergedRoute.allowed = apiRoute.allowed;
      // Do NOT override rbacEnabled from API — keep config value
      // componentPath from API takes priority (admin may have changed it via RBAC UI)
      if (apiRoute.componentPath) mergedRoute.componentPath = apiRoute.componentPath;

      if (apiRoute.tabs && typeof apiRoute.tabs === 'object') {
        Object.entries(apiRoute.tabs).forEach(([tabKey, apiTab]) => {
          if (!mergedRoute.tabs) mergedRoute.tabs = {};
          const defaultTab = mergedRoute.tabs[tabKey] || {};
          mergedRoute.tabs[tabKey] = {
            ...defaultTab,
            allowed: apiTab.allowed !== undefined ? apiTab.allowed : defaultTab.allowed,
          };

          // Merge nested sub-tabs within tabs
          if (apiTab.tabs && typeof apiTab.tabs === 'object') {
            if (!mergedRoute.tabs[tabKey].tabs) mergedRoute.tabs[tabKey].tabs = {};
            Object.entries(apiTab.tabs).forEach(([subTabKey, apiSubTab]) => {
              const defaultSubTab = mergedRoute.tabs[tabKey].tabs[subTabKey] || {};
              mergedRoute.tabs[tabKey].tabs[subTabKey] = {
                ...defaultSubTab,
                allowed: apiSubTab.allowed !== undefined ? apiSubTab.allowed : defaultSubTab.allowed,
              };
            });
          }
        });
      }

      if (apiRoute.subRoutes && typeof apiRoute.subRoutes === 'object') {
        Object.entries(apiRoute.subRoutes).forEach(([subKey, apiSub]) => {
          if (!mergedRoute.subRoutes) mergedRoute.subRoutes = {};
          const migratedKey = KEY_MIGRATION_MAP[subKey] || subKey;
          const defaultSub = mergedRoute.subRoutes[migratedKey] || mergedRoute.subRoutes[subKey] || {};
          const targetKey = mergedRoute.subRoutes[migratedKey] ? migratedKey : subKey;
          mergedRoute.subRoutes[targetKey] = {
            ...defaultSub,
            // Preserve any extra fields coming back from the API (e.g. component-level
            // RBAC flags stored under `components`). The explicit field merges below
            // still win for well-known fields, so this is a no-op for existing routes.
            ...apiSub,
            allowed: apiSub.allowed !== undefined ? apiSub.allowed : defaultSub.allowed,
            path: defaultSub.path || apiSub.path,
            componentPath: apiSub.componentPath || defaultSub.componentPath || '',
            order: apiSub.order !== undefined ? apiSub.order : defaultSub.order,
          };

          // Merge nested tabs
          if (apiSub.tabs && typeof apiSub.tabs === 'object') {
            if (!mergedRoute.subRoutes[targetKey].tabs) mergedRoute.subRoutes[targetKey].tabs = {};
            Object.entries(apiSub.tabs).forEach(([tabKey, apiTab]) => {
              const defaultTab = mergedRoute.subRoutes[targetKey].tabs[tabKey] || {};
              mergedRoute.subRoutes[targetKey].tabs[tabKey] = {
                ...defaultTab,
                allowed: apiTab.allowed !== undefined ? apiTab.allowed : defaultTab.allowed,
              };
            });
          }
        });
      }
    });
  });

  return merged;
};

/**
 * Route Permissions Store
 * Manages user route permissions fetched from profile API
 * Persists in Zustand (not localStorage)
 *
 * IMPORTANT: Two separate state slices:
 * 1. routePermissions / isLoading — Current user's permissions (drives dynamic routing)
 * 2. editingPermissions / isEditingLoading — RBAC Management editing state (does NOT affect routing)
 */
export const useRoutePermissions = create((set, get) => ({
  // ===== CURRENT USER STATE (used by useDynamicRoutes for routing) =====
  routePermissions: {},
  routeOrder: {},
  hasApiPermissions: false,  // true only when DB has real RBAC permissions for this user/role
  isLoading: true,
  isError: false,
  errorMessage: '',
  lastUpdated: null,

  // ===== EDITING STATE (used by RBAC Management — isolated from routing) =====
  editingPermissions: {},
  editingRouteOrder: {},
  isEditingLoading: false,

  // Actions

  /**
   * Fetch CURRENT USER's route permissions from /profile API
   * This drives dynamic routing — only called on app init / profile load
   */
  _isFetching: false, // internal flag to track in-flight requests

  fetchRoutePermissions: async (force = false) => {
    const state = get();
    // Skip if already loaded (has data) unless forced
    if (!force && state.lastUpdated && !state.isError) {
      return { permissions: state.routePermissions, routeOrder: state.routeOrder };
    }
    // Skip if a fetch is already in-flight
    if (state._isFetching) {
      return null;
    }

    try {
      set({ isLoading: true, _isFetching: true, isError: false, errorMessage: '' });

      const endpoint = '/profile';
      const response = await api.get(endpoint);

      const data = response?.data || {};
      const apiPermissions = data.routePermissions || {};
      const routeOrder = data.routeOrder || {};

      // Merge API permissions with config defaults
      // When real RBAC perms exist in DB: merge against EMPTY template (allowed: false)
      //   → only routes explicitly granted in DB are visible
      // When no RBAC perms in DB: use FULL template (allowed: true)
      //   → sidebar falls back to role-based siteMaps anyway (hasApiPermissions=false)
      const hasReal = Object.keys(apiPermissions).length > 0;
      const mergeBase = hasReal
        ? createEmptyRolePermissionTemplate()  // allowed: false — safe default
        : convertConfigToPermissions();         // allowed: true — for dynamic routing fallback
      const mergedPermissions = hasReal
        ? mergeWithDefaults(apiPermissions, mergeBase)
        : mergeBase;

      set({
        routePermissions: mergedPermissions,
        hasApiPermissions: hasReal,
        routeOrder,
        isLoading: false,
        _isFetching: false,
        lastUpdated: new Date().toISOString()
      });

      return { permissions: mergedPermissions, routeOrder };
    } catch (error) {
      const errorMsg = _.get(
        error,
        ['response', 'data', 'message'],
        'Failed to fetch route permissions'
      );

      set({
        isError: true,
        isLoading: false,
        _isFetching: false,
        errorMessage: errorMsg
      });

      return null;
    }
  },

  /**
   * Fetch permissions for EDITING in RBAC Management.
   * Uses separate state (editingPermissions) so it does NOT affect dynamic routing.
   * @param {string} userId - Role-prefixed ID (e.g., "role-xxx") or user ID
   * @param {number} level - Level (0 = all levels, 1-5 = specific level)
   */
  fetchPermissionsForEditing: async (userId, level = 0) => {
    if (get().isEditingLoading) return;
    try {
      set({ isEditingLoading: true });

      let response;
      let endpoint;

      if (userId && userId.startsWith('role-')) {
        const roleId = userId.replace('role-', '');
        endpoint = `/role-permissions/${roleId}?level=${level}`;
        response = await api.get(endpoint);
      } else if (userId) {
        endpoint = `/user-permissions/${userId}`;
        response = await api.get(endpoint);
      } else {
        set({ isEditingLoading: false });
        return null;
      }

      const data = response?.data || {};
      const permissions = data.route_permissions || data.routePermissions || {};
      const routeOrder = data.route_order || data.routeOrder || {};

      set({
        editingPermissions: permissions,
        editingRouteOrder: routeOrder,
        isEditingLoading: false,
      });

      return { permissions, routeOrder };
    } catch (error) {
      const errorMsg = _.get(
        error,
        ['response', 'data', 'message'],
        'Failed to fetch permissions for editing'
      );

      set({ isEditingLoading: false });
      return null;
    }
  },

  /**
   * Clear editing state (call when leaving RBAC Management)
   */
  clearEditingPermissions: () => {
    set({ editingPermissions: {}, editingRouteOrder: {} });
  },

  /**
   * Save route permissions for a user
   * @param {string} userId - User ID to save permissions for
   * @param {object} permissions - Route permissions object
   * @param {object} routeOrder - Route order configuration
   */
  saveRoutePermissions: async (userId, permissions, routeOrder = {}) => {
    try {
      const payload = {
        permissions,
        routeOrder,
        lastUpdated: new Date().toISOString()
      };

      const response = await api.put(`/user-permissions/${userId}`, payload);

      toast.success('Route permissions saved successfully!');
      return response?.data;
    } catch (error) {
      const errorMsg = _.get(
        error,
        ['response', 'data', 'message'],
        'Failed to save route permissions'
      );

      toast.error(errorMsg);
      throw error;
    }
  },

  /**
   * Update route permissions in memory (without saving to API yet)
   * Used for temporary state management before save
   */
  updateRoutePermissions: (permissions) => {
    set({ routePermissions: permissions });
  },

  /**
   * Update route order in memory
   */
  updateRouteOrder: (routeOrder) => {
    set({ routeOrder });
  },

  /**
   * Clear route permissions
   */
  clearRoutePermissions: () => {
    set({
      routePermissions: {},
      routeOrder: {},
      lastUpdated: null
    });
  },

  /**
   * Check if user has permission to access a specific route or tab
   */
  hasRoutePermission: (routeId) => {
    const state = get();
    const permissions = state.routePermissions;

    // Check if route exists in permissions
    if (typeof permissions === 'object' && permissions !== null) {
      // If permissions is a simple array/list
      if (Array.isArray(permissions.routes)) {
        return permissions.routes.includes(routeId);
      }

      // If permissions is nested object (groups > routes > tabs)
      for (const group in permissions) {
        if (typeof permissions[group] === 'object' && permissions[group].routes) {
          for (const route in permissions[group].routes) {
            if (permissions[group].routes[route].id === routeId) {
              return permissions[group].routes[route].allowed;
            }
            // Check tabs under this route
            const tabs = permissions[group].routes[route].tabs || permissions[group].routes[route].subRoutes;
            if (tabs) {
              for (const tab in tabs) {
                if (tabs[tab].id === routeId) {
                  return tabs[tab].allowed;
                }
              }
            }
          }
        }
      }
    }

    return false;
  },

  /**
   * Filter routes based on user permissions
   * Recursively removes routes/tabs/subroutes where allowed: false
   * @param {Array} routes - Route tree to filter
   * @returns {Array} Filtered routes
   */
  filterRoutesByPermissions: (routes, parentPermRoute = null) => {
    const state = get();
    const permissions = state.routePermissions;

    if (!Array.isArray(routes) || !permissions || typeof permissions !== 'object') {
      return routes; // Return as-is if no permissions set
    }

    const filterSubroutes = (subroutes, groupKey, routePath, rbacEnabled = true) => {
      if (!subroutes || typeof subroutes !== 'object') return subroutes;

      // If RBAC is disabled for this route, return all subroutes as-is
      if (!rbacEnabled) {
        return subroutes;
      }

      const group = permissions[groupKey];
      if (!group || !group.routes) return subroutes;

      const permRoute = group.routes[routePath];
      if (!permRoute || !permRoute.subRoutes) return subroutes;

      // Filter subroutes based on permissions
      const filtered = {};
      for (const subrouteKey in subroutes) {
        const subroute = subroutes[subrouteKey];
        const permSubroute = permRoute.subRoutes[subrouteKey];

        // Check if subroute is allowed in permissions
        if (permSubroute && permSubroute.allowed === false) {
          continue; // Skip this subroute
        }

        filtered[subrouteKey] = subroute;
      }

      return Object.keys(filtered).length > 0 ? filtered : null;
    };

    const filterChildren = (children, matchedGroupKey, matchedRoutePath) => {
      if (!Array.isArray(children)) return children;

      const group = permissions[matchedGroupKey];
      if (!group || !group.routes) return children; // No permission config, show all

      const permRoute = group.routes[matchedRoutePath];
      if (!permRoute || !permRoute.subRoutes) return children; // No subRoute perms, show all

      return children
        .map((child) => {
          // Match child to subRoute by path or name
          let childAllowed = true;
          let matchedSubrouteKey = null;

          for (const subrouteKey in permRoute.subRoutes) {
            const permSubroute = permRoute.subRoutes[subrouteKey];
            // Match by path or name
            if (permSubroute.path === child.to ||
                permSubroute.name === child.name ||
                (child.name && subrouteKey.toLowerCase() === child.name.toLowerCase().replace(/\s+/g, ''))) {
              childAllowed = permSubroute.allowed;
              matchedSubrouteKey = subrouteKey;
              break;
            }
          }

          // If child is not allowed, skip it
          if (!childAllowed) {
            return null;
          }

          // Recursively filter children's children
          if (child.children && Array.isArray(child.children)) {
            const filteredGrandchildren = state.filterRoutesByPermissions(child.children);
            if (filteredGrandchildren.length === 0) {
              return null; // Hide parent if all children filtered
            }
            return { ...child, children: filteredGrandchildren };
          }

          return child;
        })
        .filter(child => child !== null);
    };

    return routes
      .map((route) => {
        // Check if this route should be visible based on permissions
        let routeAllowed = true;
        let matchedGroupKey = null;
        let matchedRoutePath = null;
        let rbacEnabled = true;

        // Direct match via metadata from buildSidebarRoutes (most reliable)
        if (route._groupKey && route._routePath) {
          const group = permissions[route._groupKey];
          if (group?.routes?.[route._routePath]) {
            const permRoute = group.routes[route._routePath];
            routeAllowed = permRoute.allowed;
            rbacEnabled = permRoute.rbacEnabled !== undefined ? permRoute.rbacEnabled : true;
            matchedGroupKey = route._groupKey;
            matchedRoutePath = route._routePath;
          }
        }

        // Fallback: search through permission structure for this route
        if (!matchedGroupKey) {
          const routeToPath = (route.to || '').split('?')[0];
          let nameMatch = null; // store name match as fallback

          for (const groupKey in permissions) {
            const group = permissions[groupKey];
            if (!group || !group.routes) continue;

            for (const routePath in group.routes) {
              const permRoute = group.routes[routePath];

              // Path match (highest priority — prevents name collisions)
              if (routeToPath && permRoute.path === routeToPath) {
                routeAllowed = permRoute.allowed;
                rbacEnabled = permRoute.rbacEnabled !== undefined ? permRoute.rbacEnabled : true;
                matchedGroupKey = groupKey;
                matchedRoutePath = routePath;
                break;
              }

              // ID match (high priority)
              if (permRoute.id && route.id && permRoute.id === route.id) {
                routeAllowed = permRoute.allowed;
                rbacEnabled = permRoute.rbacEnabled !== undefined ? permRoute.rbacEnabled : true;
                matchedGroupKey = groupKey;
                matchedRoutePath = routePath;
                break;
              }

              // Name match (lower priority — save as fallback)
              if (!nameMatch && permRoute.name === route.name) {
                nameMatch = { groupKey, routePath, permRoute };
              }
            }

            if (matchedGroupKey) break;
          }

          // Use name match only if no path/ID match found
          if (!matchedGroupKey && nameMatch) {
            routeAllowed = nameMatch.permRoute.allowed;
            rbacEnabled = nameMatch.permRoute.rbacEnabled !== undefined ? nameMatch.permRoute.rbacEnabled : true;
            matchedGroupKey = nameMatch.groupKey;
            matchedRoutePath = nameMatch.routePath;
          }
        }

        // If RBAC is disabled for this route, skip all filtering and return route as-is
        if (!rbacEnabled) {
          // Still filter children recursively if they exist (they might have RBAC enabled)
          if (route.children && Array.isArray(route.children)) {
            const filteredChildren = state.filterRoutesByPermissions(route.children);
            if (filteredChildren.length === 0) {
              return route; // Return parent even if all children filtered
            }
            return { ...route, children: filteredChildren };
          }
          return route; // Return as-is
        }

        // If route itself is not allowed (and RBAC is enabled)
        // BUT has children — check if any children/subroutes are allowed first
        // e.g. parent "Configure" may have allowed:false but child "Medicines" has allowed:true
        if (!routeAllowed) {
          if (route.children && Array.isArray(route.children) && matchedGroupKey && matchedRoutePath) {
            const filteredChildren = filterChildren(route.children, matchedGroupKey, matchedRoutePath);
            if (filteredChildren.length > 0) {
              return { ...route, children: filteredChildren };
            }
          }
          return null;
        }

        // Filter subroutes within this route (for nested object structure)
        if (matchedGroupKey && matchedRoutePath && route.subRoutes) {
          const filteredSubroutes = filterSubroutes(route.subRoutes, matchedGroupKey, matchedRoutePath, rbacEnabled);

          // If all subroutes are filtered out, don't show this route either
          if (filteredSubroutes === null) {
            return null;
          }

          route = {
            ...route,
            subRoutes: filteredSubroutes
          };
        }

        // If route has children array, filter based on parent route's subRoute permissions
        if (route.children && Array.isArray(route.children) && matchedGroupKey && matchedRoutePath) {
          const filteredChildren = filterChildren(route.children, matchedGroupKey, matchedRoutePath);

          // If all children are filtered out, don't show parent either
          if (filteredChildren.length === 0) {
            return null;
          }

          return {
            ...route,
            children: filteredChildren
          };
        } else if (route.children && Array.isArray(route.children)) {
          // No permission config for this route, recursively filter children
          const filteredChildren = state.filterRoutesByPermissions(route.children);

          // If all children are filtered out, don't show parent either
          if (filteredChildren.length === 0) {
            return null;
          }

          return {
            ...route,
            children: filteredChildren
          };
        }

        return route;
      })
      .filter(route => route !== null); // Remove null entries
  }
}));

/**
 * Hook to get current user's route permissions
 * Should be called after profile is loaded
 */
export const useCurrentUserPermissions = () => {
  const { fetchRoutePermissions, ...rest } = useRoutePermissions();

  const fetchCurrentUserPermissions = async () => {
    const userId = secureLocalStorage.getItem('id');
    if (userId) {
      return await fetchRoutePermissions(userId);
    }
    return null;
  };

  return {
    ...rest,
    fetchCurrentUserPermissions
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Component-level RBAC — isolated helper
// ─────────────────────────────────────────────────────────────────────────────
//
// Purpose: lets a JSX block declare a permission key instead of hardcoding a
// role / email / level check. The hook looks up the allowed flag in the
// current user's merged routePermissions state and falls back to the registry
// default when the DB has no entry for it (fresh install, new component not
// yet seeded, user still on old pre-RBAC permissions, etc.).
//
// Today this only covers the lead-details page (registry lives in
// `src/config/leadDetailsComponents.js`). To support more pages later, add:
//   1. a new registry file like `leadDetailsComponents.js`
//   2. a new entry in `COMPONENT_REGISTRIES` below keyed by route-name
//   3. surface its toggles in the RBAC UI
//
// The hook itself does NOT need to know the shape of the route tree — it
// relies on the caller to name the route it's in.
// ─────────────────────────────────────────────────────────────────────────────

const COMPONENT_REGISTRIES = {
  'lead-details': {
    registry: LEAD_DETAILS_COMPONENTS,
    // Path in the routePermissions tree where this route's components live.
    // Must match the entry we added to routeTabConfig.js in Task 2.
    permissionsPath: ['CaseManagement', 'routes', '/case-management', 'subRoutes', 'lead-details', 'components'],
  },
};

/**
 * Read a component-level permission flag.
 *
 * @param {string} routeKey       — registry key (e.g. "lead-details")
 * @param {string} componentKey   — component key inside that registry (e.g. "smart-assist")
 * @returns {boolean}             — whether the current user is allowed to see this component
 *
 * Precedence (highest → lowest):
 *   1. DB value at routePermissions[path].components[componentKey].allowed
 *   2. Registry default (`defaultAllowed`)
 *   3. `true` (safe default if the key is not in the registry — means it was
 *      removed from the registry but the caller still references it; surface it
 *      rather than silently hide it)
 */
export const useComponentPermission = (routeKey, componentKey) => {
  const routePermissions = useRoutePermissions((s) => s.routePermissions);

  const registryEntry = COMPONENT_REGISTRIES[routeKey];
  if (!registryEntry) {
    // Unknown route key — nothing we can check against; fail open so the
    // caller stays visible (avoids silent breakage during partial rollouts).
    return true;
  }

  const { registry, permissionsPath } = registryEntry;
  const meta = registry[componentKey];
  const registryDefault = meta?.defaultAllowed;

  // Walk the permissions tree following the declared path.
  let node = routePermissions;
  for (const segment of permissionsPath) {
    if (node && typeof node === 'object' && segment in node) {
      node = node[segment];
    } else {
      node = null;
      break;
    }
  }

  const dbFlag = node && node[componentKey] ? node[componentKey].allowed : undefined;

  if (dbFlag !== undefined) return !!dbFlag;
  if (registryDefault !== undefined) return !!registryDefault;
  return true;
};

/**
 * Convenience wrapper for the most common case — lead-details components.
 *   const canShow = useLeadDetailsComponent('smart-assist');
 */
export const useLeadDetailsComponent = (componentKey) =>
  useComponentPermission('lead-details', componentKey);
